import {
  convertPxToRem,
  formatAmount,
  formatDateToReadableString,
  formatDateToReadableTime,
  getShortDayFromDate,
  getTimeZoneFromDate,
} from "@/utils";
import Panel from "../Panel";
import IconLoader from "../IconLoader";
import { Box, Divider, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import theme, { defaultFullscreenPageStyling } from "@/theme";
import CircledBackButton from "../CircledBackButton";
import { MembershipDependent } from "@/services/core-api-adapter";

interface PaymentReceiptProps {
  amount: number;
  currency: string;
  transactionDate: string;
  referenceNumber: string;
  merchantId: string;
  cardNumber: string;
  onBackButtonClick: () => void;
  isDiscountApplied?: boolean;
  originalAmount?: number;
  selectedPerson?: MembershipDependent | null;
}

export default function PaymentReceipt({
  amount,
  currency,
  transactionDate,
  referenceNumber,
  merchantId,
  cardNumber,
  onBackButtonClick,
  isDiscountApplied = false,
  originalAmount,
  selectedPerson,
}: PaymentReceiptProps) {
  const { t } = useTranslation();

  const amountFormatted = formatAmount(amount, currency);
  const formattedTransactionDate = `${getShortDayFromDate(new Date(transactionDate))}, ${formatDateToReadableString(new Date(transactionDate))}`;
  const formattedTransactionTime = formatDateToReadableTime(transactionDate);
  const formattedTransactionTimeZone = getTimeZoneFromDate(
    new Date(transactionDate)
  );

  return (
    <Stack spacing={5} sx={{ ...defaultFullscreenPageStyling }}>
      <CircledBackButton showLabel onClick={onBackButtonClick} />
      <Panel sx={{ paddingBottom: 4 }}>
        <Stack justifyContent="center" alignItems="center" spacing={4}>
          <IconLoader
            icon="UnuLogo"
            color="primary"
            sx={{ fontSize: convertPxToRem(90) }}
          />
          <Stack
            direction="row"
            width="80%"
            justifyContent="space-between"
            color="neutral.main"
          >
            <Typography variant="subtitle1" color="inherit" fontWeight={400}>
              {formattedTransactionDate}
            </Typography>
            <Divider
              sx={{ borderColor: theme.palette.neutral.main }}
              orientation="vertical"
              flexItem
            />
            <Typography variant="subtitle1" color="inherit" fontWeight={400}>
              {formattedTransactionTime}
            </Typography>
            <Divider
              sx={{ borderColor: theme.palette.neutral.main }}
              orientation="vertical"
              flexItem
            />
            <Typography variant="subtitle1" color="inherit" fontWeight={400}>
              {formattedTransactionTimeZone}
            </Typography>
          </Stack>
          <Divider sx={{ width: "100%" }} />
          <Stack alignItems="center">
            <IconLoader
              icon="TrolleyWithCheckIcon"
              color="primary"
              sx={{ fontSize: convertPxToRem(90) }}
            />
            <Typography variant="h2">{t("PaymentReceipt.title")}</Typography>
            <Typography variant="h2" color="primary">
              {amountFormatted}
            </Typography>
            {isDiscountApplied && originalAmount && (
              <Typography
                variant="body2"
                color="neutral.main"
                sx={{ textDecoration: "line-through" }}
              >
                (
                {t("common.discount.prefix", {
                  amountFormatted: formatAmount(originalAmount, currency),
                })}
                )
              </Typography>
            )}
          </Stack>
          <Stack textAlign="left" width="100%" spacing={2}>
            {selectedPerson && (
              <Box>
                <Typography variant="subtitle2">
                  {t("common.person")}
                </Typography>
                <Typography variant="h4">
                  {selectedPerson.firstName + " " + selectedPerson.lastName}
                </Typography>
              </Box>
            )}
            <Box>
              <Typography variant="subtitle2">
                {t("PaymentReceipt.referenceNumber.title")}
              </Typography>
              <Typography variant="h4">{referenceNumber}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2">
                {t("PaymentReceipt.merchantId.title")}
              </Typography>
              <Typography variant="h4">{merchantId}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2">
                {t("PaymentReceipt.cardNumber.title")}
              </Typography>
              <Typography variant="h4">{cardNumber}</Typography>
            </Box>
          </Stack>
          <Divider sx={{ width: "100%" }} />
          <Typography variant="subtitle1" color="neutral.main">
            {t("PaymentReceipt.footer.title")}
          </Typography>
        </Stack>
      </Panel>
    </Stack>
  );
}
